import React, { useState, useEffect } from 'react';
import { carriersAPI } from '../../services/api';
import Table from '../Common/Table';
import Modal from '../Common/Modal';

const CarrierManagement = () => {
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCarrier, setEditingCarrier] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    mc_number: '',
    dot_number: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'USA',
    insurance_provider: '',
    insurance_policy_number: '',
    is_active: true,
  });

  useEffect(() => {
    loadCarriers();
  }, []);

  const loadCarriers = async () => {
    try {
      setLoading(true);
      const data = await carriersAPI.getAll();
      setCarriers(data);
    } catch (error) {
      alert('Error loading carriers: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCarrier) {
        await carriersAPI.update(editingCarrier.id, formData);
      } else {
        await carriersAPI.create(formData);
      }
      setShowModal(false);
      setEditingCarrier(null);
      resetForm();
      loadCarriers();
    } catch (error) {
      alert('Error saving carrier: ' + error.message);
    }
  };

  const handleEdit = (carrier) => {
    setEditingCarrier(carrier);
    setFormData({
      company_name: carrier.company_name || '',
      mc_number: carrier.mc_number || '',
      dot_number: carrier.dot_number || '',
      contact_person: carrier.contact_person || '',
      email: carrier.email || '',
      phone: carrier.phone || '',
      address: carrier.address || '',
      city: carrier.city || '',
      state: carrier.state || '',
      zip_code: carrier.zip_code || '',
      country: carrier.country || 'USA',
      insurance_provider: carrier.insurance_provider || '',
      insurance_policy_number: carrier.insurance_policy_number || '',
      is_active: carrier.is_active !== undefined ? carrier.is_active : true,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this carrier?')) return;
    try {
      await carriersAPI.delete(id);
      loadCarriers();
    } catch (error) {
      alert('Error deleting carrier: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      company_name: '',
      mc_number: '',
      dot_number: '',
      contact_person: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'USA',
      insurance_provider: '',
      insurance_policy_number: '',
      is_active: true,
    });
  };

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'company_name', label: 'Company Name' },
    { key: 'mc_number', label: 'MC #' },
    { key: 'dot_number', label: 'DOT #' },
    { key: 'contact_person', label: 'Contact Person' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'is_active', label: 'Active', format: (val) => val ? 'Yes' : 'No' },
  ];

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Carrier Management</h1>
        <button className="btn-primary" onClick={() => { setEditingCarrier(null); resetForm(); setShowModal(true); }}>
          Add New Carrier
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <Table
          data={carriers}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      <Modal
        show={showModal}
        onClose={() => { setShowModal(false); setEditingCarrier(null); resetForm(); }}
        title={editingCarrier ? 'Edit Carrier' : 'Add New Carrier'}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Company Name *</label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              required
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>MC Number</label>
              <input
                type="text"
                value={formData.mc_number}
                onChange={(e) => setFormData({ ...formData, mc_number: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>DOT Number</label>
              <input
                type="text"
                value={formData.dot_number}
                onChange={(e) => setFormData({ ...formData, dot_number: e.target.value })}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Contact Person</label>
              <input
                type="text"
                value={formData.contact_person}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Insurance Provider</label>
              <input
                type="text"
                value={formData.insurance_provider}
                onChange={(e) => setFormData({ ...formData, insurance_provider: e.target.value })}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Insurance Policy Number</label>
            <input
              type="text"
              value={formData.insurance_policy_number}
              onChange={(e) => setFormData({ ...formData, insurance_policy_number: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Address</label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              rows="2"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>State</label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Zip Code</label>
              <input
                type="text"
                value={formData.zip_code}
                onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Country</label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              />
            </div>
          </div>
          {editingCarrier && (
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                Active
              </label>
            </div>
          )}
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => { setShowModal(false); setEditingCarrier(null); resetForm(); }}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">Save</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default CarrierManagement;


